import { FC, useContext, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { AsyncSelect, Form, FormField, ModalDialog, Numeric } from "@rothoblaas/design-system";
import FormFieldsContainer from "../../../../components/ui/FormFieldsContainer";
import VisibilityScope from "../../../../components/ui/VisibilityScope";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useAsyncOptions from "../../../../hooks/useAsyncOptions";
import useItemOptions, { getItemOptionLabel, ItemOption, toItemOption } from "../../../../hooks/useItemOptions";
import useI18n from "../../../../hooks/useTranslations";
import { ScrewDiameter } from "../../../../http/screwConnector/types/screwDiameters";
import { InsertScrewBody, Screw, UpdateScrewBody } from "../../../../http/screwConnector/types/screws";
import useScrewConnectorScrewDiametersApi from "../../../../http/screwConnector/useAdminScrewDiameters";
import useScrewConnectorScrewsApi from "../../../../http/screwConnector/useAdminScrews";

const MIN_MEASUREMENT_MM = 0.001;

interface ScrewFormBody {
  item: ItemOption | null;
  screwDiameter: ScrewDiameter | null;
  length: number | null;
  partialThreadLength: number | null;
  fullThreadExtraLength: number | null;
  tipLength: number | null;
  visibility: Screw["visibility"];
  order: number | null;
}

interface ScrewFormProps {
  screw?: Screw;
  defaultScrewDiameter?: ScrewDiameter;
  onClose: () => void;
}

const ScrewForm: FC<ScrewFormProps> = ({ screw, defaultScrewDiameter, onClose }) => {
  const i18n = useI18n();
  const t = i18n.translation.screwConnectorModule;
  const dispatch = useContext(NotificationDispatch);
  const screwsApi = useScrewConnectorScrewsApi();
  const screwDiametersApi = useScrewConnectorScrewDiametersApi();
  const itemOptions = useItemOptions();
  const screwDiameterOptions = useAsyncOptions(screwDiametersApi.getScrewDiameters);

  const [resolvedScrewDiameter, setResolvedScrewDiameter] = useState<ScrewDiameter>();

  useEffect(() => {
    if (defaultScrewDiameter || !screw) {
      setResolvedScrewDiameter(defaultScrewDiameter);
      return;
    }
    const controller = new AbortController();
    screwDiametersApi
      .getScrewDiameterById(screw.screwDiameterId, controller.signal)
      .then(setResolvedScrewDiameter)
      .catch(() => setResolvedScrewDiameter(undefined));
    return () => controller.abort();
  }, [screw?.item.id, defaultScrewDiameter]);

  const defaultValues: ScrewFormBody = {
    item: screw ? toItemOption(screw.item) : null,
    screwDiameter: resolvedScrewDiameter ?? null,
    length: screw?.length ?? null,
    partialThreadLength: screw?.partialThreadLength ?? null,
    fullThreadExtraLength: screw?.fullThreadExtraLength ?? null,
    tipLength: screw?.tipLength ?? null,
    visibility: screw?.visibility ?? "public",
    order: screw?.order ?? null,
  };

  const onSubmit = () => async (formData: ScrewFormBody) => {
    if (!formData.item || !formData.screwDiameter || formData.length == null) {
      return;
    }

    const body: InsertScrewBody | UpdateScrewBody = {
      itemId: formData.item.id,
      screwDiameterId: formData.screwDiameter.id,
      length: formData.length,
      partialThreadLength: formData.partialThreadLength,
      fullThreadExtraLength: formData.fullThreadExtraLength,
      tipLength: formData.tipLength,
      visibility: formData.visibility,
      order: formData.order ?? 0,
    };

    try {
      if (screw) {
        await screwsApi.updateScrew(screw.item.id, body);
      } else {
        await screwsApi.insertScrew(body);
      }
      dispatch(showSuccessNotification(i18n.translation));
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  return (
    <ModalDialog
      onClose={onClose}
      title={
        screw
          ? `${i18n.translation.common.edit} ${t.screws.singular}`
          : `${i18n.translation.common.add} ${t.screws.singular}`
      }
    >
      <Form<ScrewFormBody> submitText={i18n.translation.common.save} onSubmit={onSubmit} defaultValues={defaultValues}>
        {({ control, reset, setValue }) => {
          useEffect(() => {
            reset(defaultValues);
          }, [screw]);

          useEffect(() => {
            if (resolvedScrewDiameter) {
              setValue("screwDiameter", resolvedScrewDiameter);
            }
          }, [resolvedScrewDiameter]);

          return (
            <FormFieldsContainer fullWidth>
              <Controller
                name="item"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.translation.items.singular} error={fieldState.error}>
                    {({ labelId }) => (
                      <AsyncSelect<ItemOption, false>
                        {...field}
                        {...itemOptions}
                        inputId={labelId}
                        isMulti={false}
                        isDisabled={!!screw}
                        placeholder={`${i18n.translation.common.select}...`}
                        getOptionLabel={getItemOptionLabel}
                        getOptionValue={(option) => option.id}
                        isInvalid={fieldState.invalid}
                        onChange={(option) => field.onChange(option)}
                      />
                    )}
                  </FormField>
                )}
              />
              <Controller
                name="screwDiameter"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={t.screws.fields.screwDiameter} error={fieldState.error}>
                    {({ labelId }) => (
                      <AsyncSelect<ScrewDiameter, false>
                        {...field}
                        {...screwDiameterOptions}
                        inputId={labelId}
                        isMulti={false}
                        placeholder={`${i18n.translation.common.select}...`}
                        getOptionLabel={(option) => `⌀${option.diameter} ${i18n.translation.common.unitMm}`}
                        getOptionValue={(option) => option.id}
                        isInvalid={fieldState.invalid}
                        onChange={(option) => field.onChange(option)}
                      />
                    )}
                  </FormField>
                )}
              />
              <Controller
                name="length"
                control={control}
                rules={{ required: true, min: 1 }}
                render={({ field, fieldState }) => (
                  <FormField label={t.screws.fields.length} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Numeric
                        id={labelId}
                        value={field.value ?? undefined}
                        min={1}
                        decimalScale={0}
                        suffix={" " + i18n.translation.common.unitMm}
                        allowNegative={false}
                        isInvalid={isInvalid}
                        onValueChange={(value) => field.onChange(value)}
                      />
                    )}
                  </FormField>
                )}
              />
              <Controller
                name="partialThreadLength"
                control={control}
                rules={{ min: MIN_MEASUREMENT_MM }}
                render={({ field, fieldState }) => (
                  <FormField label={t.screws.fields.partialThreadLength} error={fieldState.error} isOptional>
                    {({ labelId, isInvalid }) => (
                      <Numeric
                        id={labelId}
                        value={field.value ?? undefined}
                        min={MIN_MEASUREMENT_MM}
                        decimalScale={0}
                        suffix={" " + i18n.translation.common.unitMm}
                        allowNegative={false}
                        isInvalid={isInvalid}
                        onValueChange={(value) => field.onChange(value)}
                      />
                    )}
                  </FormField>
                )}
              />
              <Controller
                name="fullThreadExtraLength"
                control={control}
                rules={{ min: MIN_MEASUREMENT_MM }}
                render={({ field, fieldState }) => (
                  <FormField label={t.screws.fields.fullThreadExtraLength} error={fieldState.error} isOptional>
                    {({ labelId, isInvalid }) => (
                      <Numeric
                        id={labelId}
                        value={field.value ?? undefined}
                        min={MIN_MEASUREMENT_MM}
                        decimalScale={0}
                        suffix={" " + i18n.translation.common.unitMm}
                        allowNegative={false}
                        isInvalid={isInvalid}
                        onValueChange={(value) => field.onChange(value)}
                      />
                    )}
                  </FormField>
                )}
              />
              <Controller
                name="tipLength"
                control={control}
                rules={{ min: MIN_MEASUREMENT_MM }}
                render={({ field, fieldState }) => (
                  <FormField label={t.screws.fields.tipLength} error={fieldState.error} isOptional>
                    {({ labelId, isInvalid }) => (
                      <Numeric
                        id={labelId}
                        value={field.value ?? undefined}
                        min={MIN_MEASUREMENT_MM}
                        decimalScale={0}
                        suffix={" " + i18n.translation.common.unitMm}
                        allowNegative={false}
                        isInvalid={isInvalid}
                        onValueChange={(value) => field.onChange(value)}
                      />
                    )}
                  </FormField>
                )}
              />
              <Controller
                name="visibility"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.translation.common.visibility} error={fieldState.error}>
                    {() => <VisibilityScope value={field.value} onChange={(value) => field.onChange(value)} />}
                  </FormField>
                )}
              />
              <Controller
                name="order"
                control={control}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.translation.common.order} error={fieldState.error} isOptional>
                    {({ labelId, isInvalid }) => (
                      <Numeric
                        id={labelId}
                        value={field.value ?? undefined}
                        decimalScale={0}
                        isInvalid={isInvalid}
                        onValueChange={(value) => field.onChange(value)}
                      />
                    )}
                  </FormField>
                )}
              />
            </FormFieldsContainer>
          );
        }}
      </Form>
    </ModalDialog>
  );
};

export default ScrewForm;
